import React, { useCallback, useState, useEffect, useMemo, useContext } from 'react'
import useRawPromise from '../../hooks/useRawPromise'
import { getUnixTime } from 'date-fns'
import FormLoader from './FormLoader'
import useBridge from '../../hooks/useBridge'
import EventDateTimePicker from '../_control/EventDateTimePicker'
import { useIntl } from 'react-intl'
import Selector from '../_control/OptionsSelector'
import NumberScroller from '../_control/NumberScroller'
import { css } from '@emotion/react'
import PitchContext from '../../context/Pitch'
import EventFormSubmit from './EventFormSubmit'

export default function AerationForm({
    eventType,
    event,
    onPostRequest,
}) {
    //utils
    const { post, put, get } = useRawPromise()
    const { formatMessage } = useIntl()
    //field state
    const [selectedDateTime, setSelectedDateTime] = useState()
    const [timeTaken, setTimeTaken] = useState(0)
    const [aerationType, setAerationType] = useState()
    const [heive, setHeive] = useState()
    const [spacing, setSpacing] = useState()
    //other
    const { data: aerationData, loading: isAerationBusy } = useBridge(`/api/current/frontend-bridge/aeration-types`)
    const { data: heiveData, loading: isHeiveBusy } = useBridge(`/api/current/frontend-bridge/aeration-heive`)
    const { data: spacingData, loading: isSpacingBusy } = useBridge(`/api/current/frontend-bridge/aeration-spacing`)
    const { data: pitch } = useContext(PitchContext)

    const [isLoading, setIsLoading] = useState(!!event)
    const [formError, setFormError] = useState()

    useEffect(() => {
        if (event) {
            async function getEventAndHydrateFormData(eventId) {
                setIsLoading(true)
                const { data } = await get(`/api/current/aeration-events/${eventId}`)
                setAerationType(data.aerationType)
                setHeive(data.aerationHeive)
                setSpacing(data.aerationSpacing)
                setTimeTaken(data.timeTaken / 60)
                setSelectedDateTime(new Date(data.timestamp * 1000))
                setIsLoading(false)
            }
            getEventAndHydrateFormData(event.id)
        }
    }, [event])

    const hasRequiredFields = useMemo(() => {
        return !!(aerationType && heive && spacing)
    }, [aerationType, heive, spacing])

    // when the required field are set the form is valid and will submit.
    const validateAndSubmit = useCallback(async () => {
        if (!Number.isFinite(timeTaken)) return setFormError(formatMessage({ id: 'pleaseEnterTimeTaken' }))
        if (!hasRequiredFields) return

        setIsLoading(true)

        const eventPayload = {
            aerationType: aerationType,
            aerationHeive: heive,
            aerationSpacing: spacing,
            timestamp: getUnixTime(selectedDateTime),
            timeTaken: parseInt(timeTaken * 60),
        }

        if (event) {
            const { data, error } = await put(`${event['@id']}`, { body: eventPayload })

            if (error) {
                setIsLoading(false)
                onPostRequest(false, error)
            }
            else if (data) {
                onPostRequest(true, data)
            }
        } else {
            const newEventPayLoad = {
                ...eventPayload,
                name: `Aeration event for pitch ${pitch['id']}`,
                pitch: pitch['@id'],
            }

            const { data, error } = await post(`/api/current/${eventType.path}`, { body: newEventPayLoad })

            if (error) {
                setIsLoading(false)
                onPostRequest(false, error)
            }
            else if (data) {
                onPostRequest(true, data)
            }
        }
    }, [pitch, aerationType, heive, spacing, selectedDateTime, timeTaken, hasRequiredFields])

    const isBusy = useMemo(() => {
        return isAerationBusy || isHeiveBusy || isSpacingBusy || isLoading
    }, [isAerationBusy, isHeiveBusy, isSpacingBusy, isLoading])

    return (<>
        {isBusy ? <FormLoader /> : <>
            <EventDateTimePicker
                unixDate={selectedDateTime}
                setUnixDate={setSelectedDateTime}
            />
            <NumberScroller
                label={formatMessage({ id: 'timeTaken' })}
                minVal={0}
                maxVal={24}
                state={timeTaken}
                unit={formatMessage({ id: 'hours' })}
                step={0.5}
                setState={setTimeTaken}
                css={css`width: min-content;`}
            />
            {aerationData &&
                <Selector
                    label={formatMessage({ id: 'aerationType' })}
                    optionsObject={aerationData}
                    state={aerationType}
                    setState={setAerationType}
                    isRequired={true}
                />
            }
            {spacingData &&
                <Selector
                    label={formatMessage({ id: 'spacing' })}
                    optionsObject={spacingData}
                    state={spacing}
                    setState={setSpacing}
                    isRequired={true}
                />
            }
            {heiveData &&
                <Selector
                    label={formatMessage({ id: 'heive' })}
                    optionsObject={heiveData}
                    state={heive}
                    setState={setHeive}
                    isRequired={true}
                />
            }
        </>}

        <EventFormSubmit
            onSubmit={validateAndSubmit}
            loading={isBusy}
            disabled={!hasRequiredFields}
            errorMsg={formError}
        />
    </>)
}